import { KnowledgeBaseApi } from '@liks/backend-svc-api/esm/apis/KnowledgeBaseApi';
import { KnowledgeBaseDocumentApi } from '@liks/backend-svc-api/esm/apis/KnowledgeBaseDocumentApi';
import { CrawlJobApi } from '@liks/backend-svc-api/esm/apis/CrawlJobApi';
import { CrawlScheduleApi } from '@liks/backend-svc-api/esm/apis/CrawlScheduleApi';
import type {
  CrawlJob,
  CrawlJobStatusDTO,
  DocumentEmbeddingProgress,
  KnowledgeBase,
  KnowledgeBaseGroupAssignment,
  KnowledgeBaseMembership,
} from '@liks/backend-svc-api/esm/models/index';
import type {
  CrawlScheduleInput,
  CrawlScheduleUpdate,
  DocumentListOptions,
  KnowledgeMemberRole,
  WithResourceId,
} from '../types/knowledge';
import { backendApiConfiguration } from './backendApi';
import * as knowledgeCrawlOps from './knowledgeCrawlOps';
import * as knowledgeCrawlScheduleOps from './knowledgeCrawlScheduleOps';
import * as knowledgeDatabaseOps from './knowledgeDatabaseOps';
import * as knowledgeDocumentOps from './knowledgeDocumentOps';
import * as knowledgeMembershipOps from './knowledgeMembershipOps';

export type * from './knowledgeServiceTypes';
class KnowledgeService {
  private readonly databaseApi = new KnowledgeBaseApi(backendApiConfiguration);
  private readonly documentApi = new KnowledgeBaseDocumentApi(
    backendApiConfiguration,
  );
  private readonly crawlApi = new CrawlJobApi(backendApiConfiguration);
  private readonly crawlScheduleApi = new CrawlScheduleApi(
    backendApiConfiguration,
  );

  listDatabases() {
    return knowledgeDatabaseOps.listDatabases(this.databaseApi);
  }

  createDatabase(name: string, description: string, icon?: string) {
    return knowledgeDatabaseOps.createDatabase(
      this.databaseApi,
      name,
      description,
      icon,
    );
  }

  getDatabase(id: string) {
    return knowledgeDatabaseOps.getDatabase(this.databaseApi, id);
  }

  updateDatabase(
    database: KnowledgeBase,
    updates: Pick<KnowledgeBase, 'name' | 'description' | 'icon'>,
  ) {
    return knowledgeDatabaseOps.updateDatabase(
      this.databaseApi,
      database,
      updates,
    );
  }

  deleteDatabase(id: string) {
    return knowledgeDatabaseOps.deleteDatabase(this.databaseApi, id);
  }

  listDocuments(databaseId: string, options?: DocumentListOptions) {
    return knowledgeDocumentOps.listDocuments(
      this.documentApi,
      databaseId,
      options,
    );
  }

  getDocument(databaseId: string, documentId: string) {
    return knowledgeDocumentOps.getDocument(
      this.documentApi,
      databaseId,
      documentId,
    );
  }

  uploadDocument(databaseId: string, file: File) {
    return knowledgeDocumentOps.uploadDocument(
      this.documentApi,
      databaseId,
      file,
    );
  }

  downloadDocument(databaseId: string, documentId: string) {
    return knowledgeDocumentOps.downloadDocument(
      this.documentApi,
      databaseId,
      documentId,
    );
  }

  deleteDocument(databaseId: string, documentId: string) {
    return knowledgeDocumentOps.deleteDocument(
      this.documentApi,
      databaseId,
      documentId,
    );
  }

  resumeAbandonedDocumentEmbedding(databaseId: string, documentId: string) {
    return knowledgeDocumentOps.resumeAbandonedDocumentEmbedding(
      this.documentApi,
      databaseId,
      documentId,
    );
  }

  streamDocumentEmbeddingProgress(
    databaseId: string,
    documentId: string,
    handlers: {
      onUpdate: (progress: DocumentEmbeddingProgress) => void;
      onComplete?: () => void;
    },
    signal?: AbortSignal,
  ) {
    return knowledgeDocumentOps.streamDocumentEmbeddingProgress(
      this.documentApi,
      databaseId,
      documentId,
      handlers,
      signal,
    );
  }

  listCrawls(databaseId: string) {
    return knowledgeCrawlOps.listCrawls(this.crawlApi, databaseId);
  }

  startCrawl(
    databaseId: string,
    startUrl: string,
    options?: {
      maxDepth?: number | null;
      maxPages?: number | null;
      useStartUrlAsPrefixFilter?: boolean | null;
      allowSubdomains?: boolean | null;
      excludedUrls?: string[] | null;
    },
  ) {
    return knowledgeCrawlOps.startCrawl(
      this.crawlApi,
      databaseId,
      startUrl,
      options,
    );
  }

  listCrawlUrlNodes(databaseId: string, crawlId: string) {
    return knowledgeCrawlOps.listCrawlUrlNodes(
      this.crawlApi,
      databaseId,
      crawlId,
    );
  }

  updateCrawlSelection(
    databaseId: string,
    crawlId: string,
    selectedNodeIds: string[],
  ) {
    return knowledgeCrawlOps.updateCrawlSelection(
      this.crawlApi,
      databaseId,
      crawlId,
      selectedNodeIds,
    );
  }

  startSelectedUrlCrawl(databaseId: string, crawlId: string) {
    return knowledgeCrawlOps.startSelectedUrlCrawl(
      this.crawlApi,
      databaseId,
      crawlId,
    );
  }

  retryFailedCrawl(
    databaseId: string,
    crawl: WithResourceId<CrawlJob>,
    options?: { scheduleId?: string },
  ) {
    return knowledgeCrawlOps.retryFailedCrawl(
      this.crawlApi,
      this.crawlScheduleApi,
      databaseId,
      crawl,
      options,
    );
  }

  restartCrawl(databaseId: string, crawlId: string) {
    return knowledgeCrawlOps.restartCrawl(this.crawlApi, databaseId, crawlId);
  }

  resumeAbandonedCrawl(databaseId: string, crawlId: string) {
    return knowledgeCrawlOps.resumeAbandonedCrawl(
      this.crawlApi,
      databaseId,
      crawlId,
    );
  }

  deleteCrawl(databaseId: string, crawlId: string) {
    return knowledgeCrawlOps.deleteCrawl(this.crawlApi, databaseId, crawlId);
  }

  getCrawlJobStatus(crawlId: string) {
    return knowledgeCrawlOps.getCrawlJobStatus(this.crawlApi, crawlId);
  }

  streamCrawlJobStatus(
    crawlId: string,
    handlers: {
      onUpdate: (status: CrawlJobStatusDTO) => void;
      onComplete?: () => void;
    },
    signal?: AbortSignal,
  ) {
    return knowledgeCrawlOps.streamCrawlJobStatus(
      this.crawlApi,
      crawlId,
      handlers,
      signal,
    );
  }

  listCrawlSchedules(databaseId: string) {
    return knowledgeCrawlScheduleOps.listCrawlSchedules(
      this.crawlScheduleApi,
      databaseId,
    );
  }

  createCrawlSchedule(databaseId: string, input: CrawlScheduleInput) {
    return knowledgeCrawlScheduleOps.createCrawlSchedule(
      this.crawlScheduleApi,
      databaseId,
      input,
    );
  }

  updateCrawlSchedule(databaseId: string, update: CrawlScheduleUpdate) {
    return knowledgeCrawlScheduleOps.updateCrawlSchedule(
      this.crawlScheduleApi,
      databaseId,
      update,
    );
  }

  deleteCrawlSchedule(databaseId: string, scheduleId: string) {
    return knowledgeCrawlScheduleOps.deleteCrawlSchedule(
      this.crawlScheduleApi,
      databaseId,
      scheduleId,
    );
  }

  listMembers(databaseId: string) {
    return knowledgeMembershipOps.listMembers(this.databaseApi, databaseId);
  }

  inviteMember(databaseId: string, userId: string, role: KnowledgeMemberRole) {
    return knowledgeMembershipOps.inviteMember(
      this.databaseApi,
      databaseId,
      userId,
      role,
    );
  }

  updateMemberRole(
    databaseId: string,
    member: KnowledgeBaseMembership,
    role: KnowledgeMemberRole,
  ) {
    return knowledgeMembershipOps.updateMemberRole(
      this.databaseApi,
      databaseId,
      member,
      role,
    );
  }

  removeMember(databaseId: string, memberId: string) {
    return knowledgeMembershipOps.removeMember(
      this.databaseApi,
      databaseId,
      memberId,
    );
  }

  listGroupAssignments(databaseId: string) {
    return knowledgeMembershipOps.listGroupAssignments(
      this.databaseApi,
      databaseId,
    );
  }

  assignGroup(
    databaseId: string,
    userGroupId: string,
    role: KnowledgeMemberRole,
  ) {
    return knowledgeMembershipOps.assignGroup(
      this.databaseApi,
      databaseId,
      userGroupId,
      role,
    );
  }

  updateGroupAssignmentRole(
    databaseId: string,
    assignment: KnowledgeBaseGroupAssignment,
    role: KnowledgeMemberRole,
  ) {
    return knowledgeMembershipOps.updateGroupAssignmentRole(
      this.databaseApi,
      databaseId,
      assignment,
      role,
    );
  }

  unassignGroup(databaseId: string, assignmentId: string) {
    return knowledgeMembershipOps.unassignGroup(
      this.databaseApi,
      databaseId,
      assignmentId,
    );
  }
}

export const knowledgeService = new KnowledgeService();
